import { Box, Fade, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { authApi } from './auth-api';
import {
    getOAuthError,
    getOAuthQueryFromSearch,
    getRedirectPath,
    redirectAfterAuth,
    getLastUsedEmail,
    setLastUsedEmail,
    clearLastUsedEmail,
    getEmailParams,
    LOGIN_REDIRECT_GUARD_KEY,
    LOGIN_REDIRECT_GUARD_WINDOW_MS,
} from './auth-utils';
import AuthSplitLayout from './AuthSplitLayout';
import AuthEmailStep from './AuthEmailStep';
import AuthLoginCredentials from './AuthLoginCredentials';
import { useAnimatedHeight } from '@/hooks/use-animated-height';
import { betterAuthClient } from '@/libs/better-auth-client';

type Step = 'email' | 'credentials';

const FADE_DURATION = 180;

const getInitialStepAndEmail = (): { step: Step; email: string } => {
    const lastMethod = betterAuthClient.getLastUsedLoginMethod();
    if (lastMethod === 'email') {
        const lastEmail = getLastUsedEmail();
        if (lastEmail) {
            return { step: 'credentials', email: lastEmail };
        }
    }
    return { step: 'email', email: '' };
};

export default function AuthLogin() {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    // Invite flow carries the invited email (and lock flag) in the query — pre-fill it over any
    // last-used email and skip straight to the password step, since the email is already known.
    const emailParams = useMemo(() => getEmailParams(), []);
    const initial = useMemo(() => {
        const base = getInitialStepAndEmail();
        return emailParams.email ? { step: 'credentials' as Step, email: emailParams.email } : base;
    }, [emailParams]);

    const [step, setStep] = useState<Step>(initial.step);
    // visibleStep trails step — it only changes after the fade-out completes,
    // so only one panel is ever mounted at a time (no height inflation).
    const [visibleStep, setVisibleStep] = useState<Step>(initial.step);

    const [email, setEmail] = useState(initial.email);

    const redirectPath = useMemo(() => getRedirectPath(), []);
    const oauthQuery = useMemo(() => getOAuthQueryFromSearch(), []);

    type BetterAuthResult<TData> = {
        data?: TData | null;
        error?: {
            message?: string;
        } | null;
    };

    const continueOAuthIfNeeded = useCallback(async (): Promise<boolean> => {
        if (!oauthQuery) {
            return false;
        }

        const result: BetterAuthResult<{ url?: string }> = await betterAuthClient.oauth2.continue({
            postLogin: true,
            oauth_query: oauthQuery,
        });

        if (result.error) {
            enqueueSnackbar(
                typeof result.error.message === 'string' ? result.error.message : t('auth.oauth.error.default'),
                { variant: 'error' },
            );
            return false;
        }

        const redirectUrl = result.data?.url;
        if (redirectUrl) {
            window.location.replace(redirectUrl);
            return true;
        }

        return false;
    }, [oauthQuery, enqueueSnackbar, t]);

    // Animate height based on the currently rendered step
    const { containerRef, contentRef, heightStyle } = useAnimatedHeight(visibleStep, 160);

    const redirectToApp = useCallback(() => {
        try {
            const last = Number(sessionStorage.getItem(LOGIN_REDIRECT_GUARD_KEY) || 0);
            if (last && Date.now() - last < LOGIN_REDIRECT_GUARD_WINDOW_MS) {
                // We just sent this visitor to the app and they're already back — /home bounced them.
                // Break the loop: drop the orphaned Better Auth session so getSession() stops matching.
                sessionStorage.removeItem(LOGIN_REDIRECT_GUARD_KEY);
                void betterAuthClient.signOut().catch(() => undefined);
                return;
            }
            sessionStorage.setItem(LOGIN_REDIRECT_GUARD_KEY, String(Date.now()));
        } catch {
            // sessionStorage unavailable (private mode / SSR) — just redirect.
        }
        redirectAfterAuth(redirectPath);
    }, [redirectPath]);

    useEffect(() => {
        authApi
            .getSession()
            .then(async (data) => {
                if (!data?.session) {
                    return;
                }

                const continued = await continueOAuthIfNeeded();
                if (!continued) {
                    redirectToApp();
                }
            })
            .catch(() => undefined);
    }, [redirectToApp, continueOAuthIfNeeded]);

    // Show a snackbar if better-auth redirected back with ?error=
    useEffect(() => {
        const oauthError = getOAuthError();
        if (!oauthError) return;
        const message = t(`auth.social.error.${oauthError.code}`, {
            defaultValue: t('auth.social.error.default'),
        });
        enqueueSnackbar(message, { variant: 'error' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEmailContinue = (submittedEmail: string) => {
        setLastUsedEmail(submittedEmail);
        setEmail(submittedEmail);
        setStep('credentials');
    };

    const handleBack = () => {
        setStep('email');
    };

    const handleUseAnotherAccount = () => {
        clearLastUsedEmail();
        setEmail('');
        setStep('email');
    };

    const handleSuccess = () => {
        void continueOAuthIfNeeded().then((continued) => {
            if (!continued) {
                redirectToApp();
            }
        });
    };

    // When step changes, wait for fade-out before swapping the rendered panel
    useEffect(() => {
        if (step === visibleStep) return;
        const timer = setTimeout(() => setVisibleStep(step), FADE_DURATION);
        return () => clearTimeout(timer);
    }, [step, visibleStep]);

    return (
        <AuthSplitLayout
            footer={
                <Typography variant="body2" color="text.secondary">
                    {t('auth.login.no-account')}{' '}
                    <Typography
                        component={Link}
                        to="/auth/register"
                        color="primary.main"
                        sx={{ textDecoration: 'none', fontWeight: 600 }}
                    >
                        {t('auth.login.go-register')}
                    </Typography>
                </Typography>
            }
        >
            <Box ref={containerRef} sx={heightStyle}>
                <Fade in={step === visibleStep} timeout={FADE_DURATION}>
                    <Box ref={contentRef}>
                        {visibleStep === 'email' ? (
                            <AuthEmailStep
                                variant="login"
                                initialEmail={email}
                                redirectPath={redirectPath}
                                oauthQuery={oauthQuery}
                                onContinue={handleEmailContinue}
                                lockEmail={emailParams.lockEmail}
                            />
                        ) : (
                            <AuthLoginCredentials
                                email={email}
                                redirectPath={redirectPath}
                                oauthQuery={oauthQuery}
                                onBack={handleBack}
                                onUseAnotherAccount={handleUseAnotherAccount}
                                onSuccess={handleSuccess}
                                lockEmail={emailParams.lockEmail}
                            />
                        )}
                    </Box>
                </Fade>
            </Box>
        </AuthSplitLayout>
    );
}
